import React from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  COVERAGE_LABELS,
  GENE_STATE,
  GENE_STATE_FILTERS,
  METHOD_KIND_LABELS,
  SCOPE_LABELS,
  UNLISTED_GENE_MEANING,
  buildGeneRows,
  countGeneStates,
  filterGeneRows,
  notCompleteReason
} from './testRecordModel';
import AddGenesDialog from './AddGenesDialog';
import GeneRowEditor from './GeneRowEditor';
import FromReportMark from './FromReportMark';
import { useTheme } from '@mui/material/styles';
import {
  ChangedField,
  ChangedFieldLabel,
  changedSx
} from 'src/components/UI/input/changed-field';
import AnchorMark from 'src/components/lab-report-verification/AnchorMark';
import {
  highlightRowSx,
  useVerification
} from 'src/components/lab-report-verification/useVerificationView';
import { geneTarget } from 'src/components/lab-report-verification/verificationTargets';
import { useAutoTranslation, translate } from 'src/hooks/useTranslate';

// A Test's genes tested (TGCLOUD-1866, Phase 2 rework): the table a panel of five genes and a panel
// of five hundred both have to be usable in.
//
// - A SEARCH and a STATE FILTER (variant reported, nothing reportable, coverage incomplete or not
//   stated), with each state's count, so "which genes were not fully covered?" is one click.
// - PAGED, 25 rows to a page by default: a 500-gene panel draws 25 rows, not 500. The rows are
//   worked out once per change of the Test (buildGeneRows), not per keystroke of the search.
// - EACH ROW EXPANDS to what the lab printed about the gene and to its variants.
// - THE METHODS SIT BESIDE EVERY STATE (Phase 1 clinical safety note, Tim 2026-09-30): the lab's
//   coverage statement is authoritative, so "nothing reportable" can mean "by sequencing only",
//   and the methods are what tell a reader which.
//
// A gene with a variant on this Test cannot be removed until its variants are: a Test that reports
// a variant in a gene it does not list would read as not having tested it.
//
// WHILE A LAB REPORT IS CHECKED BESIDE THE TEST (TGCLOUD-1868, plan D3): each row the report gave
// says where the report prints it (AnchorMark), and a row the report's selection is about is
// ringed. A box selected in the report that is about a gene on another page, or one the search or
// filter hides, brings it into view: the table moves to its page, clearing the search and filter
// only when they hide it.

const PAGE_SIZES = [25, 50, 100];

// Each label is English, translated where the chip is drawn.
const STATE_CHIP = Object.freeze({
  [GENE_STATE.VARIANT]: { color: 'info', label: () => 'Variant reported' },
  [GENE_STATE.NOTHING]: { color: 'default', label: () => 'Nothing reportable' },
  [GENE_STATE.INCOMPLETE]: { color: 'warning', label: notCompleteReason }
});

const CHIP_SX = { height: 20, fontSize: '0.7rem' };
// Read by screen readers, not shown. Strings: in `sx` a bare 1 means 100%.
const VISUALLY_HIDDEN = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px'
};
const CAPTION_SX = {
  color: 'text.secondary',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word'
};

const methodsText = (row, t = translate) =>
  (row?.methods || []).length
    ? row.methods
        .map((m) => t(METHOD_KIND_LABELS[m.kind] || METHOD_KIND_LABELS.other))
        .join(', ')
    : t('Not stated');

function GeneDetails({ row, children }) {
  const t = useAutoTranslation();

  const detail = (label, value) =>
    value ? (
      <Typography variant="caption" component="div" sx={CAPTION_SX}>
        <strong>{label}:</strong> {value}
      </Typography>
    ) : null;
  return (
    <Box sx={{ py: 1 }}>
      {detail(
        t('Transcript'),
        row.transcript && row.transcriptAsPrinted !== row.transcript
          ? row.transcriptAsPrinted
            ? t('{{transcript}} (printed {{transcriptAsPrinted}})', {
                transcript: row.transcript,
                transcriptAsPrinted: row.transcriptAsPrinted
              })
            : row.transcript
          : row.transcript || row.transcriptAsPrinted
      )}
      {row.scope !== 'whole-gene' &&
        detail(t('Region studied'), row.regionDescriptionAsPrinted)}
      {detail(t('Coverage, as printed'), row.coverage?.asPrinted)}
      {(row.methods || []).map((m, i) => (
        <Typography
          key={`${m.kind}-${i}`}
          variant="caption"
          component="div"
          sx={CAPTION_SX}
        >
          <strong>
            {t(METHOD_KIND_LABELS[m.kind]) || t(METHOD_KIND_LABELS.other)}
          </strong>
          {m.asPrinted ? `: ${m.asPrinted}` : ''}
        </Typography>
      ))}
      <Box sx={{ mt: 1 }}>{children}</Box>
    </Box>
  );
}

/**
 * @param {object} props
 * @param {object[]} props.genesTested  the Test's rows
 * @param {object[]} props.variants     the Test's variants
 * @param {boolean} [props.readOnly]    no adding, editing or removing
 * @param {Set<string>} [props.fromReport] gene keys applied from a lab report and not yet saved
 * @param {Set<string>} [props.changed]    keys of the gene rows that differ from the Test as saved
 *   (applyProposal.unsavedTestChanges), drawn as an unsaved change (TGCLOUD-1902)
 * @param {Map<string, object>} [props.savedRows] the Test's rows as saved, by key, for "Was:"
 * @param {(rows: object[]) => void} props.onAddGenes
 * @param {(key: string, row: object) => void} props.onChangeGene
 * @param {(key: string) => void} props.onRemoveGene
 * @param {(entries: Array<{variant, index}>, key: string) => React.ReactNode} props.renderVariants
 *   the gene's variants, drawn in its expanded row
 * @param {string|null} [props.unlistedState] what a gene this Test does not list reads as
 *   (testRecordModel.unlistedGeneStateOf): said when a search finds no gene (plan B2's mirror)
 */
export default function GenesTestedTable({
  genesTested,
  variants,
  readOnly = false,
  fromReport = new Set(),
  changed = new Set(),
  savedRows = new Map(),
  onAddGenes,
  onChangeGene,
  onRemoveGene,
  renderVariants,
  unlistedState = null
}) {
  const t = useAutoTranslation();
  const theme = useTheme();
  // A changed row's "Was:": the row as saved, as its columns show it; empty for a row added.
  const wasRow = (row) =>
    row
      ? [
          t(SCOPE_LABELS[row.scope]) || row.scope,
          t(COVERAGE_LABELS[row.coverage?.status || 'not-stated']) ||
            row.coverage?.status,
          methodsText(row, t)
        ]
          .filter(Boolean)
          .join(', ')
      : null;

  const [search, setSearch] = React.useState('');
  const [state, setState] = React.useState('all');
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(PAGE_SIZES[0]);
  const [expanded, setExpanded] = React.useState(() => new Set());
  const [editing, setEditing] = React.useState(null);
  const [adding, setAdding] = React.useState(false);
  const view = useVerification();
  const highlighted = (key) =>
    !!view && view.active && view.highlighted.has(geneTarget(key));

  const rows = React.useMemo(
    () => buildGeneRows(genesTested, variants),
    [genesTested, variants]
  );
  const counts = React.useMemo(() => countGeneStates(rows), [rows]);
  const shown = React.useMemo(
    () => filterGeneRows(rows, { search, state }),
    [rows, search, state]
  );
  const listedKeys = React.useMemo(
    () => new Set(rows.map((r) => r.key)),
    [rows]
  );

  // A box selected in the report, about a gene this table lists: the row is brought into view.
  const request = view && view.active ? view.focusRequest : null;
  React.useEffect(() => {
    if (!request) return;
    const wanted = new Set(request.targets || []);
    const target = rows.find((r) => wanted.has(geneTarget(r.key)));
    if (!target) return;
    let list = filterGeneRows(rows, { search, state });
    if (!list.some((r) => r.key === target.key)) {
      setSearch('');
      setState('all');
      list = rows;
    }
    const at = list.findIndex((r) => r.key === target.key);
    if (at >= 0) setPage(Math.floor(at / pageSize));
    // Asked once per request: the rows, search and page are read as they stand when it comes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  // A filter that leaves fewer rows than the page starts at goes back to the first page.
  const lastPage = Math.max(0, Math.ceil(shown.length / pageSize) - 1);
  const currentPage = Math.min(page, lastPage);
  const pageRows = shown.slice(
    currentPage * pageSize,
    currentPage * pageSize + pageSize
  );

  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const filterChip = (value, label) => (
    <Chip
      key={value}
      label={`${label} (${counts[value] || 0})`}
      size="small"
      clickable
      color={state === value ? 'secondary' : 'default'}
      variant={state === value ? 'filled' : 'outlined'}
      aria-pressed={state === value}
      onClick={() => {
        setState(value);
        setPage(0);
      }}
    />
  );

  return (
    <Box data-testid="genes-tested-table">
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          mb: 1
        }}
      >
        <TextField
          size="small"
          label={t('Search genes')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 200 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }
          }}
        />
        <Box sx={{ flex: 1 }} />
        {!readOnly && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAdding(true)}
          >
            {t('Add genes')}
          </Button>
        )}
      </Box>
      <Box
        role="group"
        aria-label={t('Show genes by state')}
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}
      >
        {filterChip('all', t('All'))}
        {GENE_STATE_FILTERS.map((f) => filterChip(f.value, t(f.label)))}
      </Box>

      {rows.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', py: 1 }}
          data-testid="no-genes-tested"
        >
          {t('No genes tested are recorded on this Test.')}
        </Typography>
      ) : (
        <TableContainer
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
        >
          <Table size="small" aria-label={t('Genes tested')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('Gene')}</TableCell>
                <TableCell>{t('Scope')}</TableCell>
                <TableCell>{t('Coverage')}</TableCell>
                <TableCell>{t('Methods')}</TableCell>
                <TableCell align="right">{t('Variants')}</TableCell>
                <TableCell align="right">
                  <Box component="span" sx={VISUALLY_HIDDEN}>
                    {t('Actions')}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                      data-testid="no-gene-matches"
                    >
                      {/* A gene searched for and not listed: what that means on this Test, so its
                          absence is never read as "not tested" where the Test never said so. */}
                      {search.trim() &&
                      state === 'all' &&
                      UNLISTED_GENE_MEANING[unlistedState]
                        ? t('No gene listed matches “{{search}}”. {{value}}', {
                            search: search.trim(),
                            value: t(UNLISTED_GENE_MEANING[unlistedState])
                          })
                        : t('No gene matches.')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {pageRows.map((r) => {
                const open = expanded.has(r.key);
                const chip = STATE_CHIP[r.state];
                const coverage = r.row.coverage?.status || 'not-stated';
                return (
                  <React.Fragment key={r.key}>
                    <TableRow
                      data-testid={`gene-row-${r.key}`}
                      data-verification-target={geneTarget(r.key)}
                      data-highlighted={highlighted(r.key) ? 'true' : undefined}
                      sx={[
                        { '& > td': { borderBottom: open ? 0 : undefined } },
                        changedSx(theme, {
                          active: changed.has(r.key),
                          variant: 'row'
                        }),
                        highlightRowSx(highlighted(r.key))
                      ]}
                    >
                      <ChangedField
                        component={TableCell}
                        variant="cell"
                        name={`gene:${r.key}`}
                        changed={changed.has(r.key)}
                        was={savedRows.get(r.key) || null}
                        formatWas={wasRow}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <IconButton
                            size="small"
                            aria-expanded={open}
                            aria-label={
                              open
                                ? t('Hide {{symbol}} details', {
                                    symbol: r.symbol
                                  })
                                : t('Show {{symbol}} details', {
                                    symbol: r.symbol
                                  })
                            }
                            onClick={() => toggle(r.key)}
                          >
                            {open ? (
                              <KeyboardArrowDownIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowRightIcon fontSize="small" />
                            )}
                          </IconButton>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {r.symbol}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5
                              }}
                            >
                              <Chip
                                size="small"
                                variant="outlined"
                                color={chip.color}
                                label={t(chip.label(r.row))}
                                sx={CHIP_SX}
                              />
                              {fromReport.has(r.key) && <FromReportMark />}
                              <AnchorMark
                                target={geneTarget(r.key)}
                                label={r.symbol}
                                sx={{ ml: 0 }}
                              />
                              <ChangedFieldLabel show={changed.has(r.key)} />
                            </Box>
                          </Box>
                        </Box>
                      </ChangedField>
                      <TableCell>
                        {t(SCOPE_LABELS[r.row.scope]) || r.row.scope}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={
                            coverage === 'complete' ? 'default' : 'warning'
                          }
                          label={t(COVERAGE_LABELS[coverage]) || coverage}
                          sx={CHIP_SX}
                        />
                      </TableCell>
                      <TableCell>{methodsText(r.row, t)}</TableCell>
                      <TableCell align="right">{r.variantCount}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {!readOnly && (
                          <>
                            <Tooltip
                              title={t('Edit {{symbol}}', { symbol: r.symbol })}
                            >
                              <IconButton
                                size="small"
                                aria-label={t('Edit {{symbol}}', {
                                  symbol: r.symbol
                                })}
                                onClick={() => setEditing(r)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                r.variants.length
                                  ? t('Remove {{symbol}}’s variants first', {
                                      symbol: r.symbol
                                    })
                                  : t('Remove {{symbol}}', { symbol: r.symbol })
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={t('Remove {{symbol}}', {
                                    symbol: r.symbol
                                  })}
                                  disabled={r.variants.length > 0}
                                  onClick={() => onRemoveGene(r.key)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow data-testid={`gene-details-${r.key}`}>
                        <TableCell colSpan={6} sx={{ pt: 0 }}>
                          <GeneDetails row={r.row}>
                            {r.variants.length ? (
                              renderVariants(r.variants, r.key)
                            ) : (
                              <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary' }}
                              >
                                {t('No variant in {{symbol}} on this Test.', {
                                  symbol: r.symbol
                                })}
                              </Typography>
                            )}
                          </GeneDetails>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          {shown.length > PAGE_SIZES[0] && (
            <TablePagination
              component="div"
              count={shown.length}
              page={currentPage}
              rowsPerPage={pageSize}
              rowsPerPageOptions={PAGE_SIZES}
              onPageChange={(_, next) => setPage(next)}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              labelRowsPerPage={t('Genes per page')}
            />
          )}
        </TableContainer>
      )}

      <AddGenesDialog
        open={adding}
        listedKeys={listedKeys}
        onClose={() => setAdding(false)}
        onAdd={(added) => {
          onAddGenes(added);
          setAdding(false);
        }}
      />
      <GeneRowEditor
        row={editing?.row || null}
        onClose={() => setEditing(null)}
        onSave={(next) => {
          onChangeGene(editing.key, next);
          setEditing(null);
        }}
      />
    </Box>
  );
}
